// ============================================================================
// TAK destinations — CRUD + connectivity test (Phase 4 item 24).
// Uses the shared axios `api` instance that already stamps the auth token.
// ============================================================================
import { api } from './api';

export type TakTransport = 'TCP' | 'TLS' | 'UDP';

/**
 * Shape returned by GET /tak-servers. The private key is NEVER included —
 * the API maps `client_key_encrypted` → `hasClientKey: boolean` so the UI
 * can render "key on file" without exposing the ciphertext.
 */
export interface TakServer {
  id: string;
  label: string;
  host: string;
  port: number;
  transport: TakTransport;
  enabled: boolean;
  hasClientCert: boolean;
  hasClientKey: boolean;
  hasTruststore: boolean;
  lastConnectedAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTakServerInput {
  label: string;
  host: string;
  port: number;
  transport: TakTransport;
  enabled?: boolean;
  // PEMs — included on create if the operator supplied them. Empty means
  // plain TCP without client auth.
  clientCertPem?: string;
  clientKeyPem?: string;
  truststorePem?: string;
}

export type UpdateTakServerInput = Partial<CreateTakServerInput>;

export interface ConnectivityTest {
  ok: boolean;
  latencyMs: number | null;
  error: string | null;
}

export const takServersApi = {
  list: async (): Promise<TakServer[]> => {
    const res = await api.get('/tak-servers');
    return res.data?.data ?? [];
  },
  get: async (id: string): Promise<TakServer> => {
    const res = await api.get(`/tak-servers/${id}`);
    return res.data.data;
  },
  create: async (input: CreateTakServerInput): Promise<TakServer> => {
    const res = await api.post('/tak-servers', input);
    return res.data.data;
  },
  update: async (id: string, input: UpdateTakServerInput): Promise<TakServer> => {
    const res = await api.patch(`/tak-servers/${id}`, input);
    return res.data.data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/tak-servers/${id}`);
  },
  test: async (id: string): Promise<ConnectivityTest> => {
    const res = await api.post(`/tak-servers/${id}/test`);
    return res.data.data;
  },
};
